import { useEffect, useState } from 'react';
import {
  Paper, TextField, Button, Typography, Grid, MenuItem, Stack, Divider, Alert,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { officerNav } from '../components/officerNav';
import api from '../services/api';
import TenderItemsEditor, { itemsFromTender, itemsToPayload } from '../components/TenderItemsEditor';

// Convert an ISO datetime to the value a datetime-local input expects.
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function toDateInput(iso) {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

export default function EditTender() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Status and isHistorical decide whether the value fields are editable.
  // Kept out of `form`, which is PUT to the server wholesale.
  const [meta, setMeta] = useState({ status: '', isHistorical: false });
  // Items are sent back with their ids so the server updates them in place.
  const [items, setItems] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/tenders/${id}`);
        const t = data.data;
        setForm({
          reference: t.reference || '',
          title: t.title || '',
          category: t.category || '',
          location: t.location || '',
          description: t.description || '',
          eligibility: t.eligibility || '',
          contactDetails: t.contactDetails || '',
          publicationDate: toDateInput(t.publicationDate),
          submissionDeadline: toLocalInput(t.submissionDeadline),
          bidOpeningDate: toLocalInput(t.bidOpeningDate),
          procurementMethod: t.procurementMethod || '',
          procurementMethodDetails: t.procurementMethodDetails || '',
          estimatedValue: t.estimatedValue ?? '',
          currency: t.currency || 'NGN',
          estimatedValueSource: t.estimatedValueSource || '',
          financialYear: t.financialYear ?? '',
        });
        setMeta({ status: t.status || '', isHistorical: Boolean(t.isHistorical) });
        setItems(itemsFromTender(t.items));
      } catch (err) {
        setError(err.response?.data?.error?.message || 'Could not load the tender.');
      }
    })();
  }, [id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setError('');
    setBusy(true);
    try {
      await api.put(`/tenders/${id}`, { ...form, items: itemsToPayload(items) });
      navigate('/officer/tenders');
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not save changes. Check the fields and try again.');
    } finally {
      setBusy(false);
    }
  };

  // The published estimate is what bidders saw. Historical records are still
  // being transcribed, and drafts are not public yet.
  const valueLocked = !meta.isHistorical && meta.status !== 'DRAFT';

  if (!form) {
    return (
      <DashboardLayout title="Edit Tender" nav={officerNav}>
        {error ? <Alert severity="error">{error}</Alert> : <Typography>Loading…</Typography>}
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Edit Tender" nav={officerNav}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Basic information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Tender Number" value={form.reference} onChange={set('reference')} fullWidth
              helperText="Must be unique. Change it only if it was entered wrongly." />
          </Grid>
          <Grid item xs={12} sm={6}><TextField label="Category" value={form.category} onChange={set('category')} fullWidth /></Grid>
          <Grid item xs={12}><TextField label="Tender Title" value={form.title} onChange={set('title')} fullWidth /></Grid>
          <Grid item xs={12}><TextField label="Location (school / community and LGA)" value={form.location} onChange={set('location')} fullWidth placeholder="e.g. JAURO SOBAI P/S, ARDOKOLA LGA" helperText="The beneficiary school or community this contract was carried out at." /></Grid>
          <Grid item xs={12}><TextField label="Description" value={form.description} onChange={set('description')} fullWidth multiline minRows={4} /></Grid>
          <Grid item xs={12}><TextField label="Eligibility criteria" value={form.eligibility} onChange={set('eligibility')} fullWidth multiline minRows={2} /></Grid>
          <Grid item xs={12}><TextField label="Contact details" value={form.contactDetails} onChange={set('contactDetails')} fullWidth /></Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Procurement details</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField select label="Procurement method" value={form.procurementMethod}
              onChange={set('procurementMethod')} fullWidth
              helperText="Leave unset if the method is not known.">
              <MenuItem value="">&mdash; Not stated &mdash;</MenuItem>
              <MenuItem value="OPEN">Open competitive bidding</MenuItem>
              <MenuItem value="SELECTIVE">Selective / restricted tendering</MenuItem>
              <MenuItem value="LIMITED">Limited tendering</MenuItem>
              <MenuItem value="DIRECT">Direct procurement</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Method as advertised" value={form.procurementMethodDetails}
              onChange={set('procurementMethodDetails')} fullWidth
              placeholder="e.g. Restricted Tendering"
              helperText="The local term used in the advertisement, if it differs." />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Estimated value" type="number" value={form.estimatedValue}
              onChange={set('estimatedValue')} fullWidth disabled={valueLocked}
              helperText={valueLocked
                ? 'Fixed: this is the estimate bidders saw when the tender was published.'
                : 'Pre-award estimate, before any award figure.'} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField label="Currency" value={form.currency} onChange={set('currency')}
              fullWidth disabled={valueLocked} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Where the figure came from" value={form.estimatedValueSource}
              onChange={set('estimatedValueSource')} fullWidth disabled={valueLocked}>
              <MenuItem value="">&mdash; Not stated &mdash;</MenuItem>
              <MenuItem value="BUDGET">Budget provision</MenuItem>
              <MenuItem value="ADVERTISED">As advertised</MenuItem>
              <MenuItem value="UNKNOWN">Not known</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Budget year" type="number" value={form.financialYear}
              onChange={set('financialYear')} fullWidth inputProps={{ min: 2000, max: 2100 }}
              helperText="The budget year this is funded from. Leave blank if not known." />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Items being procured</Typography>
        <TenderItemsEditor items={items} onChange={setItems} currency={form.currency}
          disabled={valueLocked} />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Timeline</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}><TextField label="Publication Date" type="date" value={form.publicationDate} onChange={set('publicationDate')} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
          <Grid item xs={12} sm={4}><TextField label="Submission Deadline" type="datetime-local" value={form.submissionDeadline} onChange={set('submissionDeadline')} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
          <Grid item xs={12} sm={4}><TextField label="Bid Opening Date" type="datetime-local" value={form.bidOpeningDate} onChange={set('bidOpeningDate')} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
        </Grid>
      </Paper>

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={2}>
        <Button variant="outlined" onClick={() => navigate('/officer/tenders')} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy}>Save Changes</Button>
      </Stack>
    </DashboardLayout>
  );
}